import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";
const EVENT_TYPES = ["Wedding", "Pre-wedding", "Portrait", "Event", "Commercial", "Other"];

export default function Contact() {
  const [params] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    event_type: "Wedding",
    event_date: "",
    event_time: "",
    location: "",
    message: "",
    package_id: params.get("package") || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data));
  }, []);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/bookings", form);
      setDone(true);
      toast.success("Inquiry submitted. We'll be in touch within 24 hours.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="contact-success">
      {/* UNIQUE_PAGE_HERO */}
      <PageHero heroKey="contact" image="/site-images/drive/hero-contact.jpg" mobileImage="/site-images/drive/photo-18.jpg" eyebrow="BOOK YOUR STORY" title="Your date." accent="Your story." subtitle="Tell us what you are planning. We will help shape a photography experience around it." />
        <div className="overline mb-6">Thank you</div>
        <h1 className="font-serif-display text-5xl md:text-7xl mb-8">
          Your story is on our desk.
        </h1>
        <p className="text-neutral-400 leading-relaxed max-w-xl mx-auto">
          We'll review your inquiry and reply within 24 hours with availability,
          detailed pricing and next steps. In the meantime — thank you for
          considering Wedding Touch.
        </p>
        <button
          data-testid="contact-reset"
          onClick={() => {
            setDone(false);
            setForm({ ...form, message: "" });
          }}
          className="mt-10 overline hover-underline"
        >
          Send another inquiry →
        </button>
      </div>
    );
  }

  return (
    <><SEO title="Book a Wedding Photographer in Egra | Wedding Touch" description="Contact Wedding Touch in Hosenpur, Egra to check availability and book wedding photography, pre-wedding photography, cinematic films and event coverage." canonical="/contact" /><div data-testid="contact-page" className="contact-lux-page">
      <PageHero heroKey="contact" image="/site-images/drive/hero-contact.jpg" mobileImage="/site-images/drive/photo-18.jpg" eyebrow="BOOK YOUR STORY" title="Your date." accent="Your story." subtitle="Tell us what you are planning. We will help shape a photography experience around it." />
      <div className="contact-lux-wrap max-w-6xl mx-auto px-6 lg:px-10 py-24">
      <div className="grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="overline mb-4">Book a session</div>
          <h1 className="font-serif-display text-5xl md:text-6xl mb-8">
            Tell us about
            <br />
            your&nbsp;<span className="italic text-[#E11D2A]">story</span>.
          </h1>
          <p className="text-neutral-400 leading-relaxed">
            Share a few details and we'll get back to you within 24 hours with
            availability and next steps.
          </p>
          <div className="mt-12 space-y-4 text-neutral-400">
            <div>
              <div className="overline">Email</div>
              <div>weddingtouch4@gmail.com</div>
            </div>
            <div>
              <div className="overline">Phone</div>
              <div>+91 9933420292</div>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="booking-form"
          className="lg:col-span-7 space-y-8"
        >
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <label className="overline block mb-2">Full name</label>
              <input
                required
                data-testid="booking-name"
                value={form.client_name}
                onChange={onChange("client_name")}
                className="line-input w-full py-3"
              />
            </div>
            <div>
              <label className="overline block mb-2">Email</label>
              <input
                required
                type="email"
                data-testid="booking-email"
                value={form.client_email}
                onChange={onChange("client_email")}
                className="line-input w-full py-3"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <label className="overline block mb-2">Phone</label>
              <input
                required
                data-testid="booking-phone"
                value={form.client_phone}
                onChange={onChange("client_phone")}
                className="line-input w-full py-3"
              />
            </div>
            <div>
              <label className="overline block mb-2">Event date</label>
              <input
                required
                type="date"
                data-testid="booking-date"
                value={form.event_date}
                onChange={onChange("event_date")}
                className="line-input w-full py-3 contact-date-input"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <label className="overline block mb-2">Event time</label>
              <input
                type="time"
                data-testid="booking-time"
                value={form.event_time}
                onChange={onChange("event_time")}
                className="line-input w-full py-3 [color-scheme:dark]"
              />
            </div>
            <div></div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <label className="overline block mb-2">Event type</label>
              <select
                data-testid="booking-event-type"
                value={form.event_type}
                onChange={onChange("event_type")}
                className="line-input w-full py-3"
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t} className="bg-[#0a0a0c]">
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Interested in</label>
              <select
                data-testid="booking-package"
                value={form.package_id}
                onChange={onChange("package_id")}
                className="line-input w-full py-3"
              >
                <option value="" className="bg-[#0a0a0c]">
                  Not sure yet
                </option>
                {packages.map((p) => (
                  <option key={p.id} value={p.id} className="bg-[#0a0a0c]">
                    {p.name} — ₹{p.price}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="overline block mb-2">Location</label>
            <input
              data-testid="booking-location"
              value={form.location}
              onChange={onChange("location")}
              className="line-input w-full py-3"
            />
          </div>
          <div>
            <label className="overline block mb-2">Your story</label>
            <textarea
              rows={5}
              data-testid="booking-message"
              value={form.message}
              onChange={onChange("message")}
              className="line-input w-full py-3 resize-none"
              placeholder="Tell us about the day, the mood, the people…"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="booking-submit-button"
            className="bg-white text-black px-8 py-4 text-sm uppercase tracking-[0.2em] hover:bg-neutral-200 transition-colors disabled:opacity-50"
          >
            {submitting ? "Sending…" : "Send inquiry"}
          </button>
        </form>
      </div>
      </div>
<section className="max-w-6xl mx-auto px-6 lg:px-10 pb-24">
        <div className="overline mb-4">Find Wedding Touch</div>
        <h2 className="font-serif-display text-4xl md:text-5xl mb-6">Visit us in <span className="italic text-[#E11D2A]">Egra.</span></h2>
        <p className="text-neutral-400 mb-6">Hosenpur, Egra, Purba Medinipur, West Bengal – 721429 · <a className="hover-underline" href="tel:+919933420292">+91 9933420292</a></p>
        <div className="overflow-hidden border border-white/10 rounded-sm aspect-[16/7] min-h-[320px]">
          <iframe title="Wedding Touch location in Egra" src="https://maps.google.com/maps?q=21.867472,87.530502&z=15&output=embed" width="100%" height="100%" style={{border:0,minHeight:"320px"}} loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
        </div>
      </section>
    </div></>
  );
}